import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import ContactForm from './ContactForm';

const MarketingLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, isAuthenticated } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if we should show login modal on mount (from protected route redirect)
  useEffect(() => {
    if (location.state?.showLogin) {
      setShowLoginModal(true);
      // Clear the state to prevent showing modal on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Note: Removed automatic redirect to dashboard to allow viewing marketing page when logged in

  const handleLoginSuccess = async (email, password) => {
    const result = await login(email, password);
    if (result.success) {
      // Navigate immediately without closing modal
      window.location.href = '/dashboard';
    }
    return result;
  };

  const handleStartProtection = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLaunchDashboard = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowLoginModal(true);
    }
  };

  const handlePurchase = async (plan) => {
    if (plan === 'enterprise') {
      // Scroll to contact form for enterprise
      document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    // Show payment method selection modal
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentMethod = async (method) => {
    try {
      console.log(`🛒 Initiating ${method} checkout for plan:`, selectedPlan);
      
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://cyberguard-backend-wa00.onrender.com';
      
      const endpoint = method === 'paypal' 
        ? `${API_BASE_URL}/api/payments/v1/paypal/create-order`
        : `${API_BASE_URL}/api/payments/v1/checkout/session`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: selectedPlan,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error(`Checkout failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ ${method} checkout session created:`, data);
      
      // Redirect to payment page
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No payment URL received');
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      alert('Sorry, there was an error processing your payment. Please try again or contact support.');
    } finally {
      setShowPaymentModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-slate-900/90 backdrop-blur-md border-b border-cyan-300/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <svg className="h-8 w-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                CyberGuard Global
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-gray-300 hover:text-cyan-400 transition-colors">Home</a>
              <a href="#services" className="text-gray-300 hover:text-cyan-400 transition-colors">Services</a>
              <a href="#features" className="text-gray-300 hover:text-cyan-400 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-300 hover:text-cyan-400 transition-colors">Pricing</a>
              <a href="#about" className="text-gray-300 hover:text-cyan-400 transition-colors">About</a>
              <a href="#contact" className="text-gray-300 hover:text-cyan-400 transition-colors">Contact</a>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={handleLaunchDashboard}
                    className="bg-green-500 hover:bg-green-400 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    🔒 My Dashboard
                  </button>
                  <button 
                    onClick={logout}
                    className="bg-gray-600 hover:bg-gray-500 text-white font-medium px-3 py-2 rounded-lg transition-colors text-sm"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button 
                  onClick={handleLaunchDashboard}
                  className="bg-cyan-400 hover:bg-opacity-80 text-gray-900 font-semibold px-6 py-2 rounded-lg transition-colors"
                >
                  Launch Dashboard
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-cyan-400 p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-cyan-300/30">
              <nav className="flex flex-col space-y-4">
                <a href="#home" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2">Home</a>
                <a href="#services" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2">Services</a>
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2">Features</a>
                <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2">Pricing</a>
                <a href="#about" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2">About</a>
                <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2">Contact</a>
                <div className="px-4 pt-4 border-t border-cyan-300/30">
                  {isAuthenticated ? (
                    <div className="flex flex-col space-y-2">
                      <button 
                        onClick={() => { handleLaunchDashboard(); setMobileMenuOpen(false); }}
                        className="bg-green-500 hover:bg-green-400 text-white font-semibold px-4 py-2 rounded-lg transition-colors w-full"
                      >
                        🔒 My Dashboard
                      </button>
                      <button 
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="bg-gray-600 hover:bg-gray-500 text-white font-medium px-4 py-2 rounded-lg transition-colors w-full"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => { handleLaunchDashboard(); setMobileMenuOpen(false); }}
                      className="bg-cyan-400 hover:bg-opacity-80 text-gray-900 font-semibold px-6 py-2 rounded-lg transition-colors w-full"
                    >
                      Launch Dashboard
                    </button>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-16">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/cyber-background.jpg" 
            alt="Cybersecurity Background" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-blue-900/60 to-gray-900/80"></div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            {/* Floating Shield Icon */}
            <div className="mb-8 flex justify-center">
              <div className="relative p-4 rounded-full border border-cyan-300/50 bg-gradient-to-br from-cyan-400/10 to-blue-500/10 backdrop-blur-sm glow-pulse">
                <svg className="relative h-16 w-16 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Defend Against</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Digital Threats
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Check suspicious links, files and messages before you click, catch phishing in your inbox,
              and take control of your privacy settings, using trusted services like VirusTotal and Google Safe Browsing.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={handleStartProtection}
                className={`font-semibold px-8 py-3 text-lg rounded-lg transition-colors group ${
                  isAuthenticated 
                    ? 'bg-green-500 hover:bg-green-400 text-white' 
                    : 'bg-cyan-400 hover:bg-opacity-80 text-gray-900'
                }`}
              >
                {isAuthenticated ? '🔒 View My Dashboard' : 'Launch Dashboard'}
                <svg className="ml-2 h-5 w-5 inline group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </button>
              <a href="#about" className="border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:bg-opacity-10 px-8 py-3 text-lg rounded-lg transition-colors">
                Learn More
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Powered by VirusTotal & Google Safe Browsing</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span>Read-only email access</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span>Privacy-first design</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative py-20 bg-blue-900/10 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="/cyber-background.jpg" 
            alt="Services Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Complete Security Suite
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Practical tools that check what you're unsure about, using trusted, well-known security services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Threat Intelligence",
                description: "Check a file, link or IP address before you trust it. Results come straight from established security services.",
                features: ["VirusTotal (70+ engines)", "Google Safe Browsing", "PhishTank", "AbuseIPDB"]
              },
              {
                title: "Email Protection",
                description: "Connect Gmail, Outlook or Yahoo and get flagged when an email contains a known phishing link or a dangerous attachment.",
                features: ["Read-only access", "Phishing link checks", "Attachment checks", "Review flagged emails"]
              },
              {
                title: "Scam Check",
                description: "Paste a suspicious text or email to see common scam warning signs and check its links.",
                features: ["Payment-request red flags", "Pressure and threats", "Impersonation", "Link checks"]
              },
              {
                title: "Privacy Checkup",
                description: "A guided, step-by-step review of Apple's privacy controls, for anyone who wants to lock down their iPhone.",
                features: ["Apple Safety Check", "Camera, mic & location access", "Apple Account devices", "Hidden-camera room tips"]
              },
              {
                title: "Desktop Protection",
                description: "Install the CyberGuard agent on your Mac or Windows PC and see its alerts on the web and in the app.",
                features: ["Download-and-run alerts", "Risky connection alerts", "Ransomware-rename alerts", "System health"]
              },
              {
                title: "Security Dashboard",
                description: "One place to see your checks, flagged emails, desktop alerts and next steps.",
                features: ["Protection checklist", "Lookup history", "Desktop alerts", "Web and iPhone"]
              }
            ].map((service, index) => {
              // Map service titles to their detail page URLs
              const serviceUrls = {
                "Threat Intelligence": "#features",
                "Email Protection": "#features",
                "Scam Check": "#features",
                "Privacy Checkup": "#features",
                "Desktop Protection": "/download/",
                "Security Dashboard": "#pricing"
              };
              
              return (
                <a 
                  key={index} 
                  href={serviceUrls[service.title]}
                  className="group relative bg-gray-900/50 p-6 rounded-lg border border-cyan-300/20 hover:border-cyan-300/40 transition-all duration-300 hover:scale-105 block no-underline cursor-pointer"
                >
                  <div className="mb-4">
                    <div className="w-12 h-12 bg-cyan-400/10 rounded-lg flex items-center justify-center group-hover:bg-cyan-400/20 transition-colors">
                      <svg className="h-6 w-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-cyan-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-400">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-20 bg-gray-900/20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="/cyber-background.jpg" 
            alt="Features Background" 
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/70"></div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Advanced</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Security Features
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Cutting-edge cybersecurity technology designed to stay ahead of evolving threats
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                icon: "🔎",
                title: "Trusted Sources",
                description: "Checks run against VirusTotal, Google Safe Browsing, PhishTank and AbuseIPDB, and every result shows its source"
              },
              {
                icon: "🔐",
                title: "Privacy First",
                description: "Read-only email access, and Scam Check analyzes message text on your phone"
              },
              {
                icon: "✅",
                title: "Honest Results",
                description: "If a check can't be completed, we tell you, instead of calling something safe"
              },
              {
                icon: "🔔",
                title: "Useful Alerts",
                description: "Notifications for flagged emails and alerts from your connected computers"
              }
            ].map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4 text-white group-hover:text-cyan-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Feature Highlights */}
          <div className="bg-gradient-to-r from-gray-900/50 to-blue-900/30 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-6">
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    What CyberGuard Does Not Do
                  </span>
                </h3>
                <div className="space-y-4">
                  {[
                    "Scan your iPhone for spyware. iOS doesn't allow any app to do that, so we guide you through Apple's own tools instead",
                    "Detect deepfakes or cloned voices",
                    "Replace your computer's built-in antivirus. Keep XProtect or Microsoft Defender turned on",
                    "Read, delete or move your email. Access is read-only",
                    "Guarantee a link or file is safe. \"No threats found\" means none of our sources know of a problem"
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <svg className="h-6 w-6 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-cyan-400/10 to-blue-500/10 rounded-lg p-8 border border-cyan-300/20">
                  <div className="text-center">
                    <div className="text-5xl mb-4">🤝</div>
                    <div className="text-xl text-white font-semibold mb-2">Straight answers</div>
                    <div className="text-gray-400">We'd rather tell you exactly what a check covers than promise protection no app can deliver.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="relative py-20 bg-blue-900/10 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/cyber-background.jpg" 
            alt="Why Choose Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Why Choose</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                CyberGuard Global?
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                icon: "🧭",
                title: "Easy to Use",
                description: "Paste, tap or upload, and get a clear answer in plain language"
              },
              {
                icon: "🔎",
                title: "Well-Known Sources",
                description: "Built on services security professionals already rely on"
              },
              {
                icon: "📱",
                title: "Web and iPhone",
                description: "One account across the website and the iPhone app"
              },
              {
                icon: "✉️",
                title: "Real Support",
                description: "Questions go to our team at team@cyberguardglobal.com"
              }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4 text-white group-hover:text-cyan-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-20 bg-gray-900/30 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="/cyber-background.jpg" 
            alt="Pricing Background" 
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/70"></div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Choose Your</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Protection Level
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Simple plans for individuals and teams
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Basic",
                price: "$5.95",
                description: "Everyday checks for individuals",
                features: [
                  "File, link & IP checks",
                  "Scam Check",
                  "Privacy Checkup guide",
                  "Web dashboard + iPhone app",
                  "Email support"
                ],
                plan: "basic"
              },
              {
                name: "Pro",
                price: "$14.95",
                description: "Full protection for your email and computers",
                features: [
                  "Everything in Basic",
                  "Email Protection (Gmail, Outlook, Yahoo)",
                  "Desktop Protection for Mac & Windows",
                  "Priority email support"
                ],
                popular: true,
                plan: "professional"
              },
              {
                name: "Enterprise",
                price: "Custom",
                description: "For teams. Pricing depends on team size",
                features: [
                  "Everything in Pro",
                  "Desktop Protection across your team's computers",
                  "Help getting set up",
                  "Volume pricing"
                ],
                plan: "enterprise"
              }
            ].map((tier, index) => (
              <div key={index} className={`relative p-8 rounded-lg border ${tier.popular ? 'bg-cyan-400/10 border-cyan-400 shadow-lg shadow-cyan-400/20 transform scale-105' : 'bg-gray-900/50 border-cyan-300/20'} transition-all duration-300 hover:scale-105`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-cyan-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                  <div className="text-5xl font-bold mb-2">
                    <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                      {tier.price}
                    </span>
                    {tier.price !== "Custom" && <span className="text-base text-gray-400">/month</span>}
                  </div>
                  <p className="text-gray-300">{tier.description}</p>
                </div>

                <div className="space-y-4 mb-8">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <svg className="h-5 w-5 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <button 
                    onClick={() => handlePurchase(tier.plan)}
                    className={`w-full py-3 font-semibold rounded-lg transition-colors ${
                      tier.popular 
                        ? 'bg-cyan-400 hover:bg-opacity-80 text-gray-900' 
                        : 'bg-gray-900 hover:bg-gray-800 border border-cyan-400 text-cyan-400 hover:text-white'
                    }`}
                  >
                    {tier.name === 'Enterprise' ? 'Contact Sales' : 'Get Protected'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-20 bg-blue-900/5 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/cyber-background.jpg" 
            alt="About Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-white">About</span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  CyberGuard Global
                </span>
              </h2>
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
                <p>
                  CyberGuard Global builds straightforward security tools for everyday people and small teams: checks you can run before you click, phishing alerts for your inbox, and clear guidance on using the privacy protections already built into your devices.
                </p>
                <p>
                  We focus on being useful and honest: every result tells you where it came from, and we're upfront about what an app can and can't do.
                </p>
                <p>
                  Questions? Reach our team any time at team@cyberguardglobal.com.
                </p>
              </div>
              
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-900/70 to-blue-900/30 rounded-2xl p-8 border border-cyan-300/20">
                <h3 className="text-2xl font-bold text-white mb-6">Our Core Values</h3>
                <div className="space-y-6">
                  {[
                    {
                      title: "Honesty",
                      description: "We describe exactly what each check covers, and never invent results"
                    },
                    {
                      title: "Transparent Security",
                      description: "Every result shows its source"
                    },
                    {
                      title: "Privacy",
                      description: "We ask for the least access we need, like read-only email"
                    },
                    {
                      title: "Customer Success",
                      description: "Your security goals are our primary mission"
                    }
                  ].map((value, index) => (
                    <div key={index} className="flex space-x-4">
                      <div className="w-3 h-3 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{value.title}</h4>
                        <p className="text-gray-400 text-sm">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">Get in Touch with</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Our Security Experts
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to enhance your cybersecurity? Contact us for a personalized consultation and protection plan.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-900/70 p-8 rounded-lg border border-cyan-300/20">
              <ContactForm />
              
              <div className="mt-6 pt-6 border-t border-gray-700 text-center">
                <p className="text-sm text-gray-400">
                  We typically respond within 24 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 border-t border-cyan-300/20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <svg className="h-8 w-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
                <span className="text-xl font-bold text-white">CyberGuard Global</span>
              </div>
              <p className="text-gray-400 mb-6">
                Protecting your digital world with advanced cybersecurity solutions.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.566-1.36 2.14-2.23z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white font-semibold mb-4">Services</h3>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-cyan-400 transition-colors">All Services</Link></li>
                <li><a href="/#features" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Threat Intelligence</a></li>
                <li><a href="/#features" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Email Protection</a></li>
                <li><a href="/#features" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Scam Check</a></li>
                <li><a href="/#features" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Privacy Checkup</a></li>
                <li><a href="/download/" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Desktop Protection</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-cyan-400 transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-cyan-400 transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-3">
                <li><Link to="/help" className="text-gray-400 hover:text-cyan-400 transition-colors">Help Center</Link></li>
                <li><Link to="/report-threat" className="text-gray-400 hover:text-cyan-400 transition-colors">Report Threat</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm">
                © 2024 CyberGuard Global. All rights reserved.
              </div>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 md:mt-0">
                <Link to="/privacy-policy" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors">Privacy Policy</Link>
                <Link to="/terms-of-service" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors">Terms of Service</Link>
                <Link to="/disclaimer" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors">Disclaimer</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLoginSuccess}
      />

      {/* Payment Method Selection Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-2xl max-w-md w-full border border-cyan-400/30">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Choose Payment Method</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <p className="text-gray-300 mb-6">
                Select your preferred payment method to complete your subscription:
              </p>

              <div className="space-y-4">
                {/* PayPal Button */}
                <button
                  onClick={() => handlePaymentMethod('paypal')}
                  className="w-full bg-[#0070ba] hover:bg-[#003087] text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-3 border-2 border-transparent hover:border-cyan-400"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 4.47a.77.77 0 0 1 .762-.632h8.236c1.873 0 3.197.435 3.968 1.29.804.89 1.039 2.158.67 3.748-.012.052-.027.106-.043.165a6.763 6.763 0 0 1-.305 1.03c-.53 1.34-1.504 2.323-2.895 2.923-.861.372-1.91.566-3.119.566H9.972l-.88 5.584c-.048.3-.316.525-.616.525z"/>
                    <path d="M19.175 5.784a7.073 7.073 0 0 1-.057.374c-.53 2.66-2.345 3.833-5.088 3.833h-1.29l-.88 5.584c-.048.3-.316.525-.616.525H8.29l-.88 5.584a.641.641 0 0 0 .633.74h3.935a.77.77 0 0 0 .762-.632l.675-4.289.88-5.584h1.253c2.743 0 4.558-1.172 5.088-3.833.02-.124.038-.248.054-.374z"/>
                  </svg>
                  <span>Pay with PayPal</span>
                </button>

                {/* Square Button */}
                <button
                  onClick={() => handlePaymentMethod('square')}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-3 border-2 border-transparent hover:border-cyan-400"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4.01 4.01C2.9 4.01 2 4.91 2 6.02v11.96C2 19.09 2.9 20 4.01 20h15.98c1.11 0 2.01-.91 2.01-2.02V6.02C22 4.91 21.11 4.01 19.99 4.01H4.01zm0 1.5h15.98c.28 0 .51.23.51.51v11.96c0 .28-.23.51-.51.51H4.01a.51.51 0 0 1-.51-.51V6.02c0-.28.23-.51.51-.51z"/>
                    <path d="M7 9h10v6H7z"/>
                  </svg>
                  <span>Pay with Card (Square)</span>
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                  </svg>
                  <span>Secure checkout powered by SSL encryption</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingLanding;